import { Button, Form, Input, Modal, Space, notification } from "antd";
import type { FormProps } from "antd";
import { useEffect, useState } from "react";
import { getCloudService } from "../../backend";

import { useParams } from "react-router";

type RequiredMark = FormProps['requiredMark'];

export interface ConnectExoscaleAccountModalProps {
    accountName?: string;
    accountId?: string;
    visible?: boolean;
    onOk?: (id: string) => void;
    onCancel?: () => void;
}

const ConnectExoscaleAccountModal = ({
    accountName,
    accountId,
    visible,
    onOk,
    onCancel
}: ConnectExoscaleAccountModalProps) => {
    const { projectId } = useParams() as { projectId: string; };
    const [form] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [updating, setUpdating] = useState(false);
    const [modalVisible, setModalVisible] = useState(visible);

    useEffect(() => {
        setModalVisible(visible);
    }, [visible]);

    const handleAccountAction = async () => {
        try {
            setUpdating(true);
            if (accountName && accountId) {
                // Update only allows changing accountName
                const values = await form.validateFields() as { accountName: string; };
                await getCloudService().updateExoscaleAccount(
                    accountId,
                    projectId,
                    {
                        accountName: values.accountName
                    }
                );
                onOk?.(accountId);
            } else {
                const values = await form.validateFields() as { accountName: string; apiKey: string; apiSecret: string; };
                const { data } = await getCloudService().addExoscaleAccount(
                    projectId,
                    {
                        accountName: values.accountName,
                        apiKey: values.apiKey,
                        apiSecret: values.apiSecret
                    }
                );
                onOk?.(data.id);
            }
        } catch (error) {
            const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Unknown error occurred';
            notification.error({
                message: "Failed to connect Exoscale account",
                description: errorMessage
            });
        } finally {
            setUpdating(false);
        }
    }

    return (
        <Modal
            title="Connect Exoscale Account"
            centered
            closable={false}
            open={modalVisible}
            footer={[
                <div className="flex-justify-space-between flex-align-center">
                    <a href="https://docs.microtica.com/connect-an-exoscale-account" target="_blank" rel="noreferrer">
                        How to connect Exoscale account?
                    </a>
                    <Space>
                        <Button onClick={() => {
                            setModalVisible(false);
                            onCancel?.();
                        }}
                        >
                            Close
                        </Button>
                        <Button
                            type="primary"
                            loading={updating}
                            onClick={handleAccountAction}
                        >
                            {
                                accountName && accountId ? "Update" : "Connect"
                            }
                        </Button>
                    </Space>
                </div>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    accountName: accountName || '',
                    apiKey: ''
                }}
                requiredMark={requiredMark}
            >
                <Form.Item
                    name="accountName"
                    label="Name"
                    required
                    rules={[{ required: true, message: 'Please input the cloud account name!' }]}
                >
                    <Input />
                </Form.Item>
                {
                    !accountId && (
                        <>
                            <Form.Item
                                name="apiKey"
                                label="API Key"
                                required
                                rules={[
                                    { required: true, message: 'Please input the Exoscale API key!' }
                                ]}
                                extra="Create this manually in Exoscale with preferred role permissions"
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="apiSecret"
                                label="API Secret"
                                required
                                rules={[
                                    { required: true, message: 'Please input the Exoscale API secret!' }
                                ]}
                                extra="Create this manually in Exoscale with preferred role permissions"
                            >
                                <Input.Password />
                            </Form.Item>
                        </>
                    )
                }
            </Form>
        </Modal>
    )
}

export default ConnectExoscaleAccountModal;

